import React, { useState } from 'react';
import Image from 'next/image';
import axios from '../src/lib/axios';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Flex,
  Icon,
  List,
  ListItem,
  ListIcon,
  Input,
  Link,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  ArrowForwardIcon,
  CheckIcon,
  TimeIcon,
  StarIcon,
  InfoIcon,
  LockIcon,
  QuestionIcon,
} from '@chakra-ui/icons';
import Head from 'next/head';
import AppIcon from '../src/components/common/AppIcon';
import { appConfig } from '../src/lib/config';

// 共有カード（OGP）と検索結果に出る文言。ここだけ直せば全チャネルに反映される
const SEO = {
  title: 'LogMe（ログミー）| ナイトワーク店舗の勤怠・バック計算・給与・会計・シフト',
  description:
    '紙伝票・手計算から卒業。勤怠・バック計算・給与・会計・シフトを一つに。キャストも自分のスマホで確認できます。キャバクラ・ガールズバー・ラウンジ・スナック対応。',
  url: `${appConfig.siteUrl}/`,
  image: `${appConfig.siteUrl}/ogp.png`,
  imageAlt: 'LogMe — ナイトワーク店舗の業務を、これ一つで。',
} as const;

export default function LogMeLandingPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const track = (event: string, data?: Record<string, unknown>) => {
    console.info(`[LogMe Track] ${event}`, data ?? {});
  };

  const scrollToForm = () => {
    track('lp_cta_click_free_trial');
    document.getElementById('early-access')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!email) { setEmailError('メールアドレスを入力してください'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailError('正しいメールアドレスを入力してください'); return; }
    setEmailError('');
    setIsSubmitting(true);
    track('lp_form_submit_early_access', { email });
    try {
      await axios.post('/api/early-access', { email, website: '' });
      setIsSubmitting(false);
      setSubmitted(true);
      track('lp_form_submit_early_access_success', { email });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      const status = axiosErr.response?.status;
      let message: string;
      if (status === 429) {
        message = '送信回数の上限に達しました。しばらく待ってからお試しください。';
      } else if (status === 422) {
        message = axiosErr.response?.data?.message ?? '正しいメールアドレスを入力してください。';
      } else {
        message = axiosErr.response?.data?.message ?? '送信に失敗しました。もう一度お試しください。';
      }
      setEmailError(message);
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    track('lp_post_submit_click_reset');
    setSubmitted(false);
  };

  return (
    <Box minH="100vh" bg={bgColor} color={textColor}>
      <Head>
        <title>{SEO.title}</title>
        <meta name="description" content={SEO.description} />
        <link rel="canonical" href={SEO.url} />

        {/* Open Graph（LINE / Facebook / Slack の共有カード） */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={appConfig.wordmark} />
        <meta property="og:locale" content="ja_JP" />
        <meta property="og:url" content={SEO.url} />
        <meta property="og:title" content={SEO.title} />
        <meta property="og:description" content={SEO.description} />
        <meta property="og:image" content={SEO.image} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={SEO.imageAlt} />

        {/* X（Twitter）。summary_large_image にしないと小さい正方形で出る */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO.title} />
        <meta name="twitter:description" content={SEO.description} />
        <meta name="twitter:image" content={SEO.image} />
        <meta name="twitter:image:alt" content={SEO.imageAlt} />
      </Head>

      {/* Header */}
      <Box
        position="sticky"
        top={0}
        zIndex={40}
        bg="white"
        borderBottom="1px"
        borderColor={borderColor}
        backdropFilter="blur(10px)"
      >
        <Container maxW="7xl" px={4} py={4}>
          <Flex align="center" justify="space-between">
            <HStack spacing={2}>
              <AppIcon size={9} />
              <Heading size="lg" letterSpacing="-0.02em">{appConfig.wordmark}</Heading>
              <Text fontSize="sm" color={mutedColor} display={{ base: 'none', sm: 'inline' }}>
                ナイトワーク向け 勤怠・売上・バック・会計SaaS
              </Text>
            </HStack>
            <HStack spacing={6} display={{ base: 'none', md: 'flex' }}>
              <Link href="#pain" _hover={{ color: 'gray.700' }}>よくある課題</Link>
              <Link href="#features" _hover={{ color: 'gray.700' }}>特長</Link>
              <Link href="#screens" _hover={{ color: 'gray.700' }}>画面</Link>
              <Link href="#pricing" _hover={{ color: 'gray.700' }}>料金</Link>
              <Link href="#faq" _hover={{ color: 'gray.700' }}>FAQ</Link>
            </HStack>
            <HStack spacing={3}>
              <Button variant="outline" borderRadius="2xl" as="a" href="/login">ログイン</Button>
              <Button borderRadius="2xl" colorScheme="blue" onClick={scrollToForm}>無料で試す</Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Hero */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="7xl" px={4}>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={8} alignItems="center">
            <GridItem>
              <VStack align="start" spacing={6}>
                <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
                  Free β 公開中 — 今なら無料で始められます
                </Badge>
                <Heading
                  as="h1"
                  size={{ base: 'xl', md: '2xl' }}
                  fontWeight="extrabold"
                  lineHeight="shorter"
                >
                  紙伝票・手計算から{' '}
                  <Text as="span" bgGradient="linear(to-r, blue.600, purple.600)" bgClip="text">
                    卒業
                  </Text>
                  。
                  <br />
                  ナイトワーク店舗の
                  <br />
                  業務を、これ一つで。
                </Heading>
                <Text fontSize={{ base: 'lg', md: 'xl' }} color={mutedColor} lineHeight="tall">
                  勤怠・バック計算・給与・会計・シフトを一つに。
                  <br />
                  キャストも自分のスマホで確認できる。
                </Text>
                <Text fontSize="md" color={mutedColor}>
                  キャバクラ・ガールズバー・ラウンジ・スナック対応。
                  <br />
                  メールアドレスだけで始められます。
                </Text>
                <HStack spacing={3} wrap="wrap">
                  <Button size="lg" borderRadius="2xl" colorScheme="blue" onClick={scrollToForm}>
                    無料で試す <ArrowForwardIcon ml={1} />
                  </Button>
                  <Button size="lg" borderRadius="2xl" variant="outline" as="a" href="#screens">
                    画面を見る
                  </Button>
                </HStack>
                <HStack spacing={6} fontSize={{ base: 'sm', md: 'md' }} color={mutedColor}>
                  <HStack spacing={2}>
                    <Icon as={LockIcon} w={4} h={4} />
                    <Text>GCPセキュア基盤</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Icon as={CheckIcon} w={4} h={4} />
                    <Text>インボイス対応</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Icon as={TimeIcon} w={4} h={4} />
                    <Text>スマホ/タブレット対応</Text>
                  </HStack>
                </HStack>
              </VStack>
            </GridItem>
            <GridItem>
              <Card borderRadius="2xl" shadow="md" p={2} overflow="hidden">
                <Image
                  src="/lp/ticket.png"
                  alt="LogMe フロア伝票ボード"
                  width={960}
                  height={600}
                  style={{ width: '100%', height: 'auto', borderRadius: '12px' }}
                  priority
                />
              </Card>
              <Text mt={3} fontSize="xs" color={mutedColor}>
                ※ 実際の管理画面です
              </Text>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* Pain Points — Before / After */}
      <Box id="pain" bg="gray.50" py={{ base: 14, md: 20 }}>
        <Container maxW="7xl" px={4}>
          <VStack spacing={8} align="center" mb={10}>
            <Heading as="h2" size={{ base: 'xl', md: '2xl' }} textAlign="center">
              こんな悩み、ありませんか？
            </Heading>
            <Text fontSize={{ base: 'md', md: 'xl' }} color={mutedColor} textAlign="center">
              月末の計算作業を丸一日 → 30分に。ナイトワーク店舗の「あるある」をLogMeが解決します。
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {painPoints.map((item, i) => (
              <Card key={i} borderRadius="2xl" shadow="sm" overflow="hidden">
                <CardBody>
                  <VStack align="start" spacing={4}>
                    <HStack spacing={3} align="start">
                      <Badge flexShrink={0} colorScheme="red" fontSize="xs" px={2} py={1} borderRadius="full">Before</Badge>
                      <Text fontSize={{ base: 'md', md: 'lg' }} color="red.600" fontWeight="semibold" lineHeight="tall">
                        {item.before}
                      </Text>
                    </HStack>
                    <Box w="full" h="1px" bg="gray.200" />
                    <HStack spacing={3} align="start">
                      <Badge flexShrink={0} colorScheme="green" fontSize="xs" px={2} py={1} borderRadius="full">After</Badge>
                      <VStack align="start" spacing={1}>
                        <Text fontSize={{ base: 'md', md: 'lg' }} color="green.600" fontWeight="semibold" lineHeight="tall">
                          {item.after}
                        </Text>
                        <Text fontSize="sm" color={mutedColor}>{item.feature}</Text>
                      </VStack>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Trust */}
      <Box py={14}>
        <Container maxW="7xl" px={4}>
          <VStack spacing={8} align="center">
            <Heading as="h2" size={{ base: 'lg', md: 'xl' }} textAlign="center">
              無料でも、安心して使えます
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full">
              {trustCards.map((card, i) => (
                <Card key={i} borderRadius="2xl" shadow="sm">
                  <CardBody>
                    <VStack align="start" spacing={3}>
                      <HStack spacing={2}>
                        <Icon as={card.icon} w={5} h={5} color="gray.700" />
                        <Text fontWeight="bold" fontSize="md">{card.title}</Text>
                      </HStack>
                      <Text fontSize={{ base: 'sm', md: 'lg' }} color={mutedColor} lineHeight="tall">
                        {card.desc}
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Features — 店舗オペレーション */}
      <Box id="features" bg="gray.50" py={16}>
        <Container maxW="7xl" px={4}>
          <VStack spacing={10} align="center" mb={10}>
            <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">店舗オペレーション</Badge>
            <Heading size={{ base: 'xl', md: '2xl' }} textAlign="center">
              フロア管理から会計まで、一気通貫
            </Heading>
            <Text fontSize={{ base: 'md', md: 'xl' }} color={mutedColor} textAlign="center">
              チェックイン → オーダー → 会計 → レシート印刷。すべてブラウザ上で完結。
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {storeFeatures.map((feature, index) => (
              <Card key={index} borderRadius="2xl">
                <CardHeader>
                  <HStack spacing={3}>
                    <Box p={2} borderRadius="xl" border="1px" borderColor={borderColor} bg="white">
                      <Icon as={feature.icon} w={5} h={5} />
                    </Box>
                    <Heading size="md">{feature.title}</Heading>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <List spacing={2}>
                    {feature.points.map((point, idx) => (
                      <ListItem key={idx} fontSize={{ base: 'sm', md: 'lg' }} color={mutedColor}>
                        <ListIcon as={CheckIcon} color="green.500" />
                        {point}
                      </ListItem>
                    ))}
                  </List>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Cast Portal */}
      <Box py={{ base: 16, md: 20 }}>
        <Container maxW="7xl" px={4}>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={10} alignItems="center">
            <GridItem order={{ base: 2, md: 1 }}>
              <Box maxW="400px" mx="auto">
                <Image
                  src="/lp/cast-home.png"
                  alt="LogMe キャストのマイページ（スマホ）"
                  width={400}
                  height={700}
                  style={{ width: '100%', height: 'auto' }}
                />
              </Box>
            </GridItem>
            <GridItem order={{ base: 1, md: 2 }}>
              <VStack align="start" spacing={6}>
                <Badge colorScheme="purple" fontSize="sm" px={3} py={1} borderRadius="full">キャストポータル</Badge>
                <Heading size={{ base: 'xl', md: '2xl' }} lineHeight="shorter">
                  キャストが自分で確認。
                  <br />
                  「バック合ってる？」が
                  <br />
                  なくなる。
                </Heading>
                <Text fontSize={{ base: 'md', md: 'lg' }} color={mutedColor} lineHeight="tall">
                  キャスト専用画面で、自分のスマホから出退勤・バック・給与をリアルタイムに確認。
                  シフト希望もアプリから提出できます。
                </Text>
                <List spacing={3}>
                  {castFeatures.map((feature, idx) => (
                    <ListItem key={idx} fontSize={{ base: 'md', md: 'lg' }} color={mutedColor}>
                      <ListIcon as={CheckIcon} color="green.500" />
                      {feature}
                    </ListItem>
                  ))}
                </List>
              </VStack>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* Features — 給与・シフト管理 */}
      <Box bg="gray.50" py={16}>
        <Container maxW="7xl" px={4}>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={10} alignItems="center">
            <GridItem>
              <VStack align="start" spacing={6}>
                <Badge colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full">給与・シフト管理</Badge>
                <Heading size={{ base: 'xl', md: '2xl' }} lineHeight="shorter">
                  月末の給与計算、
                  <br />
                  ワンクリックで完了。
                </Heading>
                <Text fontSize={{ base: 'md', md: 'lg' }} color={mutedColor} lineHeight="tall">
                  勤怠・バック・控除を自動集計。ランク別の時給設定や日払い/月次にも対応。
                  シフト希望→承認のワークフローでLINEのやり取りも不要に。
                </Text>
                <List spacing={3}>
                  {payrollFeatures.map((feature, idx) => (
                    <ListItem key={idx} fontSize={{ base: 'md', md: 'lg' }} color={mutedColor}>
                      <ListIcon as={CheckIcon} color="green.500" />
                      {feature}
                    </ListItem>
                  ))}
                </List>
              </VStack>
            </GridItem>
            <GridItem>
              <Card borderRadius="2xl" shadow="md" p={2} overflow="hidden">
                <Image
                  src="/lp/payroll.png"
                  alt="LogMe 給与計算画面"
                  width={960}
                  height={600}
                  style={{ width: '100%', height: 'auto', borderRadius: '12px' }}
                />
              </Card>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* Features — 管理・セキュリティ */}
      <Box py={16}>
        <Container maxW="7xl" px={4}>
          <VStack spacing={10} align="center" mb={10}>
            <Badge colorScheme="orange" fontSize="sm" px={3} py={1} borderRadius="full">管理・セキュリティ</Badge>
            <Heading size={{ base: 'xl', md: '2xl' }} textAlign="center">
              安心して任せられる管理機能
            </Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {adminFeatures.map((feature, index) => (
              <Card key={index} borderRadius="2xl">
                <CardBody>
                  <VStack align="start" spacing={3}>
                    <Box p={2} borderRadius="xl" border="1px" borderColor={borderColor} bg="white">
                      <Icon as={feature.icon} w={5} h={5} />
                    </Box>
                    <Text fontWeight="bold" fontSize="md">{feature.title}</Text>
                    <Text fontSize={{ base: 'sm', md: 'md' }} color={mutedColor} lineHeight="tall">
                      {feature.desc}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Mid CTA */}
      <Box bg="blue.50" py={10}>
        <Container maxW="4xl" px={4}>
          <VStack spacing={4} textAlign="center">
            <Heading size={{ base: 'md', md: 'lg' }}>
              月末の手計算、もうやめませんか？
            </Heading>
            <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.600">
              Free βなら1店舗・人数無制限で無料。まずは触ってみてください。
            </Text>
            <Button size="lg" borderRadius="2xl" colorScheme="blue" onClick={scrollToForm}>
              無料で試す <ArrowForwardIcon ml={1} />
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Screens */}
      <Box id="screens" bg="gray.50" py={16}>
        <Container maxW="7xl" px={4}>
          <VStack spacing={10} align="center" mb={10}>
            <Heading size={{ base: 'xl', md: '2xl' }} textAlign="center">
              画面イメージ
            </Heading>
            <Text fontSize={{ base: 'md', md: 'xl' }} color={mutedColor} textAlign="center">
              直感的。すぐに使える。現場フローに沿ったUI。
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {screens.map((screen, index) => (
              <Card key={index} borderRadius="2xl" overflow="hidden">
                <CardBody p={0}>
                  <Image
                    src={screen.image}
                    alt={screen.label}
                    width={960}
                    height={600}
                    style={{ width: '100%', height: 'auto' }}
                  />
                  <Box p={5}>
                    <Text fontWeight="bold" fontSize="lg" mb={1}>{screen.label}</Text>
                    <Text fontSize={{ base: 'sm', md: 'md' }} color={mutedColor} lineHeight="tall">
                      {screen.desc}
                    </Text>
                  </Box>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Flow */}
      <Box py={16}>
        <Container maxW="7xl" px={4}>
          <VStack spacing={10} align="center" mb={10}>
            <Heading size={{ base: 'xl', md: '2xl' }} textAlign="center">
              導入までの流れ
            </Heading>
            <Text fontSize={{ base: 'md', md: 'xl' }} color={mutedColor} textAlign="center">
              最短当日スタート。既存データも移行サポート。
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
            {flowSteps.map((step, index) => (
              <Card key={index} borderRadius="2xl" position="relative">
                <CardHeader>
                  <HStack spacing={3}>
                    <Box
                      w={8} h={8}
                      borderRadius="full"
                      bg="blue.500"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      fontSize="sm"
                      flexShrink={0}
                    >
                      {index + 1}
                    </Box>
                    <Heading size="md">{step.title}</Heading>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <Text fontSize={{ base: 'sm', md: 'lg' }} color={mutedColor} lineHeight="tall">
                    {step.desc}
                  </Text>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Pricing */}
      <Box id="pricing" bg="gray.50" py={16}>
        <Container maxW="7xl" px={4}>
          <VStack spacing={10} align="center" mb={10}>
            <Heading size={{ base: 'xl', md: '2xl' }} textAlign="center">
              料金プラン
            </Heading>
            <Text fontSize={{ base: 'md', md: 'xl' }} color={mutedColor} textAlign="center">
              料金プランは3つ。無料のFree βから始められます。
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {pricingPlans.map((plan, index) => (
              <Card
                key={index}
                borderRadius="2xl"
                border={plan.highlight ? "2px" : "1px"}
                borderColor={plan.highlight ? "blue.500" : borderColor}
              >
                <CardHeader>
                  <Flex justify="space-between" align="center">
                    <Heading size="md">{plan.name}</Heading>
                    {plan.highlight && (
                      <Badge colorScheme="blue" fontSize="xs" px={2} py={1} borderRadius="full">
                        おすすめ
                      </Badge>
                    )}
                  </Flex>
                </CardHeader>
                <CardBody>
                  <VStack align="start" spacing={6}>
                    <Box>
                      <Text fontSize="3xl" fontWeight="bold">
                        {plan.price}
                      </Text>
                      <Text fontSize={{ base: 'sm', md: 'md' }} color={mutedColor} mt={1}>
                        {plan.note}
                      </Text>
                    </Box>
                    <List spacing={2} w="full">
                      {plan.features.map((feature, idx) => (
                        <ListItem key={idx} fontSize={{ base: 'sm', md: 'lg' }} color={mutedColor}>
                          <ListIcon as={CheckIcon} color="green.500" />
                          {feature}
                        </ListItem>
                      ))}
                    </List>
                    <Button w="full" borderRadius="2xl" colorScheme={plan.highlight ? "blue" : undefined} onClick={scrollToForm}>
                      このプランで始める
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Subsidy */}
      <Box id="subsidy" py={{ base: 16, md: 20 }}>
        <Container maxW="4xl" px={4}>
          <VStack spacing={6} align="center" mb={12}>
            <Heading size={{ base: 'xl', md: '2xl' }} textAlign="center" lineHeight="tall">
              「高そう…」と思った方へ。
              <br />
              補助金が使える可能性があります
            </Heading>
            <VStack spacing={4} align="start" w="full">
              <Text
                fontSize={{ base: 'md', md: 'xl' }}
                color={mutedColor}
                lineHeight="tall"
              >
                logmeのようなITツールの導入には、国や自治体の補助金が使える場合があります。
                「IT導入補助金」などの制度を活用すれば、初期費用や月額料金の負担を大きく抑えられる可能性があります。
              </Text>
              <Text fontSize={{ base: 'sm', md: 'md' }} color={mutedColor} lineHeight="tall">
                ※ 補助金は公募期間が決まっているため、タイミングによって使えない場合もあります。
                ただし、まずはご相談いただければ「今対象になりそうか」をお調べします。
              </Text>
            </VStack>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={12}>
            {subsidyMerits.map((merit, i) => (
              <Card key={i} borderRadius="2xl" shadow="sm">
                <CardBody>
                  <VStack align="start" spacing={4}>
                    <Text fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }}>{merit.title}</Text>
                    <Text fontSize={{ base: 'sm', md: 'lg' }} color={mutedColor} lineHeight="tall">
                      {merit.desc}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>

          <VStack spacing={4} mb={12}>
            {subsidyFaq.map((item, i) => (
              <Card key={i} borderRadius="2xl" w="full">
                <CardHeader>
                  <HStack spacing={2}>
                    <Icon as={QuestionIcon} w={4} h={4} color="blue.500" />
                    <Heading size="md" color={textColor}>{item.q}</Heading>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <Text fontSize={{ base: 'sm', md: 'lg' }} color={mutedColor} lineHeight="tall">
                    {item.a}
                  </Text>
                </CardBody>
              </Card>
            ))}
          </VStack>

          <Text fontSize={{ base: 'sm', md: 'md' }} color={mutedColor} textAlign="center" mb={10}>
            ※ 補助金の採択には審査があり、申請すれば必ず通るものではありません。制度の詳細・要件は各公募回により異なります。
          </Text>

          <VStack spacing={4} align="center" py={4}>
            <Button
              size="lg"
              borderRadius="2xl"
              px={10}
              onClick={scrollToForm}
            >
              補助金の対象か確認する
            </Button>
            <Text fontSize={{ base: 'sm', md: 'lg' }} color={mutedColor}>
              無料でお調べします。まずは「対象になるか」の確認だけでOKです。
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* FAQ */}
      <Box id="faq" bg="gray.50" py={16}>
        <Container maxW="4xl" px={4}>
          <VStack spacing={10} align="center" mb={10}>
            <Heading size={{ base: 'xl', md: '2xl' }} textAlign="center">
              よくある質問
            </Heading>
          </VStack>
          <VStack spacing={4}>
            {faqItems.map((item, index) => (
              <Card key={index} borderRadius="2xl" w="full">
                <CardHeader>
                  <Heading size="md">{item.q}</Heading>
                </CardHeader>
                <CardBody>
                  <Text fontSize={{ base: 'sm', md: 'lg' }} color={mutedColor} lineHeight="tall">
                    {item.a}
                  </Text>
                </CardBody>
              </Card>
            ))}
          </VStack>
        </Container>
      </Box>

      {/* CTA */}
      <Box id="early-access" borderTop="1px" borderColor={borderColor} py={16}>
        <Container maxW="7xl" px={4}>
          <VStack spacing={6} textAlign="center">
            <Heading size={{ base: 'xl', md: '2xl' }}>
              まずは無料で試してみる
            </Heading>
            <Text fontSize={{ base: 'md', md: 'xl' }} color={mutedColor}>
              メールアドレスだけで始められます。後から店舗数や機能は拡張可能。
            </Text>
            <Text fontSize={{ base: 'sm', md: 'lg' }} color="blue.600" fontWeight="semibold">
              Free β：1店舗・人数無制限で無料
            </Text>
            {submitted ? (
              <Card borderRadius="2xl" shadow="sm" w={{ base: 'full', sm: 'md' }} textAlign="left">
                <CardBody>
                  <VStack spacing={4} align="start">
                    <HStack spacing={2}>
                      <Icon as={CheckIcon} color="green.500" w={5} h={5} />
                      <Text fontWeight="bold" fontSize="lg">お申し込みありがとうございます！</Text>
                    </HStack>
                    <Text fontSize={{ base: 'sm', md: 'lg' }} color={mutedColor} lineHeight="tall">
                      入力いただいたメールアドレス宛にご案内をお送りします。
                      先にログイン画面から操作感をご確認いただけます。
                    </Text>
                    <Text fontSize={{ base: 'sm', md: 'md' }} color={mutedColor}>
                      ※営業メールは送りません（必要な連絡のみ）
                    </Text>
                    <Flex gap={3} w="full" direction={{ base: 'column', sm: 'row' }}>
                      <Button
                        borderRadius="2xl"
                        as="a"
                        href="/login"
                        onClick={() => track('lp_post_submit_click_login')}
                        flex={1}
                      >
                        ログイン画面へ
                      </Button>
                      <Button
                        variant="outline"
                        borderRadius="2xl"
                        onClick={handleReset}
                        flex={1}
                      >
                        もう一度メールを送る
                      </Button>
                    </Flex>
                  </VStack>
                </CardBody>
              </Card>
            ) : (
              <>
                {/* Honeypot: bot対策（ユーザーには見えない） */}
                <input
                  type="text"
                  name="website"
                  autoComplete="off"
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                />
                <HStack spacing={3} w={{ base: 'full', sm: 'auto' }}>
                  <Input
                    placeholder="メールアドレス"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                    w={{ base: 'full', sm: '320px' }}
                    borderRadius="2xl"
                    h="44px"
                    isInvalid={!!emailError}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                  />
                  <Button
                    borderRadius="2xl"
                    h="44px"
                    px={6}
                    colorScheme="blue"
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                  >
                    無料で試す
                  </Button>
                </HStack>
                {emailError && (
                  <Text fontSize="sm" color="red.500">{emailError}</Text>
                )}
              </>
            )}
            <Text fontSize={{ base: 'sm', md: 'md' }} color={mutedColor}>
              送信することで、<Link href="/privacy" textDecoration="underline" _hover={{ color: 'blue.500' }}>プライバシーポリシー</Link>に同意したものとみなします。
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Footer */}
      <Box borderTop="1px" borderColor={borderColor} py={10}>
        <Container maxW="7xl" px={4}>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6} fontSize={{ base: 'sm', md: 'md' }}>
            <GridItem>
              <VStack align="start" spacing={2}>
                <HStack spacing={2}>
                  <AppIcon size={7} />
                  <Text fontWeight="bold" letterSpacing="-0.02em">{appConfig.wordmark}</Text>
                </HStack>
                <Text color={mutedColor}>ナイトワーク店舗の業務を、これ一つで。</Text>
                <Text color={mutedColor}>
                  © {new Date().getFullYear()} LogMe. All rights reserved.
                </Text>
              </VStack>
            </GridItem>
            <GridItem>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="semibold">プロダクト</Text>
                  <VStack align="start" spacing={2} color={mutedColor}>
                    <Link href="#pain" _hover={{ textDecoration: 'underline' }}>よくある課題</Link>
                    <Link href="#features" _hover={{ textDecoration: 'underline' }}>特長</Link>
                    <Link href="#screens" _hover={{ textDecoration: 'underline' }}>画面</Link>
                    <Link href="#pricing" _hover={{ textDecoration: 'underline' }}>料金</Link>
                    <Link href="#faq" _hover={{ textDecoration: 'underline' }}>FAQ</Link>
                  </VStack>
                </VStack>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="semibold">サポート</Text>
                  <VStack align="start" spacing={2} color={mutedColor}>
                    <Link href="#" _hover={{ textDecoration: 'underline' }}>ヘルプセンター</Link>
                    <Link href="mailto:support@logmeapp.com" _hover={{ textDecoration: 'underline' }}>お問い合わせ</Link>
                    <Link href="/terms" _hover={{ textDecoration: 'underline' }}>利用規約</Link>
                    <Link href="/privacy" _hover={{ textDecoration: 'underline' }}>プライバシー</Link>
                  </VStack>
                </VStack>
              </Grid>
            </GridItem>
            <GridItem>
              <VStack align="start" spacing={2} color={mutedColor}>
                <Text fontWeight="semibold">連携・対応</Text>
                <VStack align="start" spacing={2}>
                  <HStack spacing={2}>
                    <Icon as={CheckIcon} w={4} h={4} />
                    <Text>インボイス制度対応（適格請求書）</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Icon as={TimeIcon} w={4} h={4} />
                    <Text>レシート印刷（80mm/58mm サーマル）</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Icon as={StarIcon} w={4} h={4} />
                    <Text>マルチ店舗・部門対応</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Icon as={InfoIcon} w={4} h={4} />
                    <Text>スマホ / タブレット / PC対応</Text>
                  </HStack>
                </VStack>
              </VStack>
            </GridItem>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

// ─── Data ───────────────────────────────────────────────

const painPoints = [
  {
    before: '紙伝票の手計算で、月末は毎回キャストと揉める',
    after: 'バック・指名・ドリンクを自動集計。明細も即時確認',
    feature: '→ バック自動計算・キャストポータル',
  },
  {
    before: '月末の給与計算にスタッフ2人で丸一日かかる',
    after: 'ワンクリックで全キャストの給与を一括計算',
    feature: '→ 給与計算（日払い/月次・時給/バック/控除を自動集計）',
  },
  {
    before: 'キャストから「バック合ってる？」と毎回聞かれる',
    after: 'キャスト自身がスマホでリアルタイム確認',
    feature: '→ キャストポータル（出退勤・バック・給与確認）',
  },
  {
    before: 'シフト集めがLINEグループのやり取りで大変',
    after: 'シフト希望→承認をシステム上で完結',
    feature: '→ シフト管理（希望提出・承認ワークフロー）',
  },
  {
    before: '誰がいつ何を操作したか分からず、トラブル時に追えない',
    after: '全操作を自動記録。監査ログで即時追跡',
    feature: '→ 監査ログ・異常検知',
  },
  {
    before: 'インボイス対応で伝票の書き換えが面倒',
    after: '適格請求書番号・税率区分を自動印字',
    feature: '→ インボイス対応レシート/領収書',
  },
];

const trustCards = [
  {
    icon: LockIcon,
    title: 'データは店舗ごとに安全に管理',
    desc: '店舗・キャストのデータは完全に分離され、他店舗から閲覧されることはありません。',
  },
  {
    icon: InfoIcon,
    title: '見える人・触れる人を制御',
    desc: '管理者・スタッフ・キャストで操作・閲覧権限を明確に分離できます。',
  },
  {
    icon: CheckIcon,
    title: 'いつでも解約・データ削除可能',
    desc: 'β版期間中も料金はかからず、不要になればいつでも停止できます。',
  },
];

const storeFeatures = [
  {
    icon: StarIcon,
    title: 'フロア伝票ボード',
    points: [
      'テーブル別の伝票・滞在分数・会計状況を可視化',
      'チェックイン→オーダー→会計まで一気通貫',
      '混雑時でも一目で状況把握、配席ミス削減',
    ],
  },
  {
    icon: TimeIcon,
    title: '会計/インボイス',
    points: [
      '会計締め・レシート/領収書を即時発行',
      'インボイス対応（適格請求書）',
      '割引・サービス料・税率設定も自在',
    ],
  },
  {
    icon: InfoIcon,
    title: 'バック自動計算',
    points: [
      '売上バック・指名・同伴・ドリンクを自動集計',
      'ランク別・店舗別のルールに柔軟対応',
      '日次/月次の明細出力・承認フロー',
    ],
  },
];

const castFeatures = [
  'スマホで出退勤（ワンタップ打刻）',
  '今日のバック・売上をリアルタイム確認',
  '給与明細をいつでも閲覧',
  'シフト希望をアプリから提出',
  'LINE連携でログインも簡単',
];

const payrollFeatures = [
  '勤怠・バック・控除を自動集計してワンクリック計算',
  'ランク別・個人別の時給を自動適用（設定は一度だけ）',
  '日払い / 月次のどちらにも対応',
  'シフト希望→承認ワークフロー（ドラッグ&ドロップ計画）',
  '締め前チェックで計算漏れを防止',
];

const adminFeatures = [
  {
    icon: LockIcon,
    title: 'ロール別権限',
    desc: '管理者・スタッフ・キャストの3ロールに加え、機能単位の細かい権限設定が可能。',
  },
  {
    icon: InfoIcon,
    title: '監査ログ・異常検知',
    desc: '全操作を自動記録。異常な操作パターンも自動検出してログに残します。',
  },
  {
    icon: StarIcon,
    title: 'マルチ店舗・組織階層',
    desc: '会社→事務所→店舗の階層管理。店舗ごとにルール・メニュー・控除を個別設定。',
  },
  {
    icon: LockIcon,
    title: 'セキュリティ',
    desc: 'GCP上のセキュア構成。2段階認証・パスワードポリシー・IP制限（Pro）。',
  },
];

const screens = [
  {
    label: 'フロア伝票ボード',
    desc: 'テーブルごとの滞在時間・担当キャスト・会計状況をリアルタイムに一覧。チェックインから会計まで全てこの画面で完結します。',
    image: '/lp/ticket.png',
  },
  {
    label: 'フロア図',
    desc: 'テーブルレイアウトを視覚的に表示。空席・使用中・会計待ちが一目でわかり、配席ミスを防止します。',
    image: '/lp/floor.png',
  },
  {
    label: '給与計算',
    desc: '全キャストの給与を一括計算。時給・控除・バック連携を自動処理。日払い/月次の切り替えも対応。',
    image: '/lp/payroll.png',
  },
  {
    label: 'シフト管理',
    desc: 'シフト希望の提出→承認をシステム上で完結。ドラッグ&ドロップで直感的に計画を組めます。',
    image: '/lp/shift.png',
  },
  {
    label: '会計・レシート',
    desc: '会計締め後、レシート・領収書をワンクリック発行。インボイス（適格請求書）にも対応。サーマルプリンターへの直接印刷も可能。',
    image: '/lp/receipt.png',
  },
  {
    label: '監査ログ',
    desc: '誰が・いつ・何を操作したかを自動記録。トラブル時の追跡や不正防止に。異常検知ログとも連携。',
    image: '/lp/audit.png',
  },
];

const flowSteps = [
  { title: '要件ヒアリング', desc: '店舗規模・営業形態・バックルールを確認。オンラインで30分程度。' },
  { title: '初期設定', desc: 'テーブル/メニュー/バック率/権限/店舗をまとめて登録。最短当日完了。' },
  { title: 'レシート連携', desc: '既存プリンターやPOSと連携設定（任意）。80mm/58mmサーマル対応。' },
  { title: '運用開始', desc: 'スタッフ・キャストを招待して本番運用スタート。サポート体制も継続。' },
];

const pricingPlans = [
  {
    name: 'Free β',
    price: '¥0 / 月',
    note: 'ずっと無料・基本業務のみ',
    features: ['1店舗・人数無制限', '会計・勤怠・月次給与', 'キャストポータル', 'メールサポート（優先外）'],
  },
  {
    name: 'Standard',
    price: '¥9,800 / 店・月',
    note: '複数店舗は店舗数ぶん',
    features: ['全機能・人数無制限', 'リアルタイム日払い・バック自動計算', 'レシート/インボイス・給与・シフト', '優先サポート・データエクスポート'],
    highlight: true,
  },
  {
    name: 'Pro',
    price: '¥29,800 / 月〜',
    note: '多店舗チェーン・個別見積',
    features: ['Standardの全機能', 'API連携・SLA対応', '監査ログ強化・IP制限・SAML', '異常検知・高度な分析'],
  },
];

const subsidyMerits = [
  {
    title: '導入費用が1/2〜2/3程度になるケースも',
    desc: '「思っていたより負担が軽かった」と言われることが多いポイントです。給与計算やバック管理のシステム化を、少ない負担で始められます。',
  },
  {
    title: '紙伝票・手計算から卒業',
    desc: '補助金を使って、計算ミスやスタッフとの揉め事の原因をまとめて解消できます。',
  },
  {
    title: '申請のサポートあり',
    desc: '「難しそう」と思っても大丈夫。対象になるかの確認から、私たちがお手伝いします。',
  },
];

const subsidyFaq = [
  {
    q: '補助金って難しくない？',
    a: '申請には書類の準備が必要ですが、logmeチームが手続きをサポートします。まずは「対象になりそうか」だけ確認してみませんか？',
  },
  {
    q: 'うちみたいな小さい店でも対象？',
    a: 'はい、従業員数名の個人店舗でも対象になるケースは多くあります。業種や規模で諦める前に、一度ご相談ください。',
  },
  {
    q: 'いつもらえるの？',
    a: '補助金制度には申請期間（公募回）があり、年に複数回チャンスがあります。採択後、実際の交付は導入・報告の完了後になるのが一般的です。時期はお問い合わせ時に最新スケジュールをご案内します。',
  },
  {
    q: '結局いくら安くなる？',
    a: '制度や審査結果によりますが、目安として導入費用の1/2〜2/3程度が補助されるケースがあります。例えば月額利用料2年分が対象になる場合もあり、実質負担を大幅に減らせる可能性があります。',
  },
];

const faqItems = [
  {
    q: '既存のレシートプリンターと連携できますか？',
    a: 'はい、Star Micronics製などの一般的な80mm/58mmサーマルプリンターに対応しています。CloudPRNTによるクラウド印刷で、ブラウザから直接レシート発行が可能です。',
  },
  {
    q: 'インボイス（適格請求書）に対応していますか？',
    a: '対応しています。適格請求書発行事業者番号や税率区分の印字、レシート/領収書の書式もテンプレートで調整できます。',
  },
  {
    q: '店舗ごとのバックルールに対応できますか？',
    a: '売上バック・指名/同伴・ドリンク・延長等、店舗ごとのルール定義をマスターで柔軟に設定できます。ランク別のティア設定にも対応しています。',
  },
  {
    q: 'キャストが自分のスマホから使えますか？',
    a: 'はい。キャスト専用ポータルがあり、出退勤打刻・バック確認・給与明細閲覧・シフト希望提出がスマホから可能です。LINEログインにも対応しています。',
  },
  {
    q: '給与計算はどこまで自動化されますか？',
    a: '勤怠（時給×勤務時間）+ バック + 控除を自動集計し、ワンクリックで全キャストの給与を一括計算できます。日払い/月次の切り替えにも対応。',
  },
  {
    q: 'データのエクスポートや既存システムとの連携は？',
    a: 'CSV/JSONでの出力に加え、API連携で会計・BI・人事給与など外部システムと接続できます（Standardプラン以上）。',
  },
  {
    q: '複数店舗を一括管理できますか？',
    a: 'はい。会社→事務所→店舗の階層構造で管理でき、店舗ごとに異なるメニュー・バックルール・控除設定が可能です。',
  },
];
